import React, { useState } from 'react';
import { PopupOrPanel } from '../../../components/popupsAndPanels/PopupAndPanels';
import { WondeApi } from 'api/wondeApi';
import { MisChangeAnswerResponse, MIS_CHANGE_NOT_FOUND_CODE, hasWondeMessageCode } from '../models';

type Props = {
  misChangeMessage: string;
  onAnswered: (answer: MisChangeAnswerResponse) => void;
  onClose: () => void;
};

export const MisChangePopup: React.FC<Props> = ({
  misChangeMessage,
  onAnswered,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  // Set when a No looks like a real MIS change, nothing is saved until the
  // school picks Yes or chooses to continue with No
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const submitAnswer = async (
    bConfirmedMisChange: boolean,
    bContinueAfterWarning: boolean = false
  ) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    const result = await WondeApi.answerMisChange(
      bConfirmedMisChange,
      bContinueAfterWarning
    );

    if (!result.error && result.data?.bShowWarning) {
      setWarningMessage(result.data.warningMessage);
      setIsSubmitting(false);
      return;
    }

    if (!result.error && result.data) {
      onAnswered(result.data);
      return;
    }

    setError(
      hasWondeMessageCode(result.error?.formErrors, MIS_CHANGE_NOT_FOUND_CODE)
        ? 'This MIS change has already been answered. Please close this message and sync again.'
        : 'An error occurred, please try again.'
    );
    setIsSubmitting(false);
  };

  if (warningMessage) {
    return (
      <PopupOrPanel
        isPopup={true}
        isOpen={true}
        hdrTitle="Are you sure?"
        position="right"
        givenClass="WondeMisChange"
        primaryBtnTxt="Yes, we have changed MIS"
        secondaryBtnTxt="No, continue"
        handleClose={() => !isSubmitting && onClose()}
        handlePrimaryBtn={() => submitAnswer(true)}
        handleSecondaryBtn={() => submitAnswer(false, true)}
        width={460}
        isSubmitting={isSubmitting}
      >
        {error && <p className="GeneralError2">{error}</p>}
        <p>{warningMessage}</p>
      </PopupOrPanel>
    );
  }

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={true}
      hdrTitle="MIS Change"
      position="right"
      givenClass="WondeMisChange"
      primaryBtnTxt="Yes"
      secondaryBtnTxt="No"
      handleClose={() => !isSubmitting && onClose()}
      handlePrimaryBtn={() => submitAnswer(true)}
      handleSecondaryBtn={() => submitAnswer(false)}
      width={460}
      isSubmitting={isSubmitting}
    >
      {error && <p className="GeneralError2">{error}</p>}
      <p>{misChangeMessage}</p>
    </PopupOrPanel>
  );
};
